/**
 * Customer list grid — admin-facing list of all tenants (customers).
 *
 * Server-backed via EntityGrid over odl-app/customers/. Column order, sizing,
 * visibility, and pinning all come from the backend grid-spec descriptor; FE
 * field overrides only supply renderers — the logo icon column, the name/
 * description link cell, the deactivated status chip, and the padded ID cell.
 * Status and type filters drive server-side filter[...] params via extraParams.
 * The stat cards above the grid are declared by the same descriptor, so which
 * of them a customer type is offered is configuration rather than code; their
 * numbers come from odl-app/customers/stats/ and recompute whenever the active
 * filters change.
 * The toolbar's CSV export writes the on-screen columns for every customer the
 * active filters match, not just the loaded page.
 *
 * Contains:
 * - CustomerGrid     — EntityGrid over odl-app/customers/ (default export)
 * - CustomerLogo     — height-constrained logo image with business-icon fallback
 * - CustomerListItem — mobile card
 */

import BusinessIcon from '@mui/icons-material/Business';
import { Box, Chip } from '@mui/material';
import { blue, green } from '@mui/material/colors';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { fetchCustomersStats } from '@sophocles/api/customer';
import FilterBar from '@sophocles/components/FilterBar';
import { buildStatusQuickFilter } from '@sophocles/components/FilterBar/statusQuickFilter';
import { EntityGrid } from '@sophocles/data-grids';
import GridActiveCell from '@sophocles/data-grids/components/cells/GridActiveCell';
import GridNameCell from '@sophocles/data-grids/components/cells/GridNameCell';
import GridMobileCard from '@sophocles/data-grids/components/mobile/GridMobileCard';
import { toJsonApiParams } from '@sophocles/data-grids/datasource/params';
import useColumnVisibilityModel from '@sophocles/data-grids/datasource/useColumnVisibilityModel';
import { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridStats from '@sophocles/data-grids/datasource/useGridStats';
import useStatCardOrderModel from '@sophocles/data-grids/datasource/useStatCardOrderModel';
import useStatCardVisibilityModel from '@sophocles/data-grids/datasource/useStatCardVisibilityModel';
import { selectSeparateTenant, selectTenantInfo } from '@sophocles/features/tenant/slice';
import usePersistedState from '@sophocles/hooks/usePersistedState';
import useTenantTerms from '@sophocles/hooks/useTenantTerms';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// A customer's logo is how the row is recognized before its name is read, so
// the phone card gives it several times the area the desktop column can spare.
// The fallback icon stays small: an absent logo should close the gap, not hold
// a large empty square open.
const MOBILE_LOGO_HEIGHT = 48;
const MOBILE_LOGO_MAX_WIDTH = 132;
const MOBILE_LOGO_FALLBACK_SIZE = 30;

// Customer logo: a height-constrained image at its natural aspect ratio
// (logos are often wide/landscape, so a square avatar would crop or
// distort them). Capped width keeps a very wide logo from blowing out the
// column. Falls back to a business icon when there's no logo.
function CustomerLogo({ row, size = 24, maxWidth = 72, fallbackSize = size, sx }) {
  const [failed, setFailed] = useState(false);
  if (row.logo && !failed) {
    return (
      <Box
        component="img"
        src={row.logo}
        alt={row.name}
        onError={() => setFailed(true)}
        sx={{ height: size, width: 'auto', maxWidth, objectFit: 'contain', display: 'block', flexShrink: 0, ...sx }}
      />
    );
  }
  return <BusinessIcon sx={{ fontSize: fallbackSize, color: 'text.secondary', flexShrink: 0, ...sx }} />;
}

function CustomerListItem({ row, typeLabel }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const supportReference = `#${row.id?.toString()?.padStart(5, '0') ?? row.id}`;

  return (
    <GridMobileCard
      onClick={() => navigate(`/c/${row.id}/plants`)}
      leading={
        <CustomerLogo
          row={row}
          size={MOBILE_LOGO_HEIGHT}
          maxWidth={MOBILE_LOGO_MAX_WIDTH}
          fallbackSize={MOBILE_LOGO_FALLBACK_SIZE}
        />
      }
      primary={row.name}
      secondary={row.description}
      meta={[{ key: 'id', label: t('general.id', 'ID'), value: supportReference }]}
      chips={typeLabel ? [<Chip key="type" size="small" variant="outlined" label={typeLabel} />] : null}
      status={<GridActiveCell deactivated={row.deactivated} />}
    />
  );
}

// ---------------------------------------------------------------------------
// Main grid component
// ---------------------------------------------------------------------------

export default function CustomerGrid() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tenantInfo = useSelector(selectTenantInfo);
  const separateTenant = useSelector(selectSeparateTenant);

  const [statusFilter, setStatusFilter] = usePersistedState(useGridSessionKey('customerGrid', 'status'), 'active');
  const [typeFilter, setTypeFilter] = usePersistedState(useGridSessionKey('customerGrid', 'type'), null);

  const columnVisibility = useColumnVisibilityModel('customerGrid');
  const statCardVisibility = useStatCardVisibilityModel('customerGrid');
  const statCardOrder = useStatCardOrderModel('customerGrid');
  const tenantTerms = useTenantTerms();

  // extraParams drives both the EntityGrid list fetch and the stats fetch. Each
  // key becomes filter[key] on the wire via toJsonApiParams (run inside
  // EntityGrid for the list, and explicitly below for the stats call).
  const extraParams = useMemo(() => {
    const params = {};
    if (statusFilter === 'active') params.deactivated = false;
    else if (statusFilter === 'deactivated') params.deactivated = true;
    // statusFilter === 'all': no deactivated filter → backend returns all
    if (typeFilter != null) params.customer_type_id = typeFilter;
    return params;
  }, [statusFilter, typeFilter]);

  // Stats cards: separate fetch keyed on extraParams so they recompute with
  // active filters. The stats endpoint honors the same filter[...] params as
  // the list endpoint; toJsonApiParams converts plain keys to the wire format.
  const statsParams = useMemo(() => toJsonApiParams(extraParams), [extraParams]);
  const { stats: gridStats, isLoading: statsLoading } = useGridStats(fetchCustomersStats, statsParams);

  // Only types a visible customer actually carries, so picking one can never
  // land on an empty grid. The counts ignore the type filter itself (see the
  // stats action) — selecting a type leaves every other type still offered.
  // The count rides beside the name rather than inside it: it is a different
  // fact about a different thing, and the bar reads it down its own column.
  const typeOptions = useMemo(
    () =>
      (gridStats?.customer_type_counts ?? []).map(type => ({
        value: type.id,
        label: type.name,
        count: type.count,
      })),
    [gridStats],
  );

  // Render overrides on top of descriptor column defaults. Only intentional
  // display decisions — sizing, order, and headers come from the descriptor.
  const fieldOverrides = useMemo(
    () => ({
      // Pad the raw id to a stable 5-digit support reference.
      id: {
        cellClassName: 'cell-pinned',
        renderCell: ({ value }) => value?.toString()?.padStart(5, '0') ?? value,
      },
      // Logo is its own pinned column (descriptor-driven); render the icon.
      logo: {
        cellClassName: 'cell-pinned',
        renderCell: ({ row }) => <CustomerLogo row={row} size={28} />,
        align: 'center',
        headerAlign: 'center',
      },
      name: {
        cellClassName: 'cell-pinned',
        renderCell: ({ row, value }) => (
          <GridNameCell to={`/c/${row.id}/plants`} primary={value} secondary={row.description} />
        ),
      },
      deactivated: {
        renderCell: ({ value }) => <GridActiveCell deactivated={value} />,
      },
    }),
    [],
  );

  // The descriptor's trailing "select" action deep-links to the customer's
  // home (its plants grid) — same destination as the name cell and mobile
  // card. Rendered as a contained button (variant: 'button') rather than the
  // default icon so it reads as the row's primary affordance.
  const actionHandlers = useMemo(
    () => ({ select: { onClick: row => navigate(`/c/${row.id}/plants`), variant: 'button' } }),
    [navigate],
  );

  const quickFilters = useMemo(
    () => [
      buildStatusQuickFilter({ t, value: statusFilter, onChange: setStatusFilter }),
      // Type filter is hidden for separate tenants (only "Commercial" applies).
      ...(separateTenant
        ? []
        : [
            {
              key: 'type',
              label: t('customer.customerType', 'Type'),
              type: 'single',
              allLabel: t('general.all', 'All Types'),
              options: typeOptions,
              value: typeFilter,
              onChange: setTypeFilter,
            },
          ]),
    ],
    [t, statusFilter, typeFilter, typeOptions, setStatusFilter, setTypeFilter, separateTenant],
  );

  // Export overrides: `deactivated` reads as a status on screen, so the file
  // states the same Active/Deactivated words rather than the raw flag's Yes/No.
  // Everything else exports off its descriptor type.
  const exportOptions = useMemo(
    () => ({
      fileName: 'customers.csv',
      columns: {
        deactivated: {
          valueGetter: row => (row.deactivated ? t('general.deactivated') : t('general.active', 'Active')),
        },
      },
    }),
    [t],
  );

  const handleResetAll = useCallback(() => {
    setStatusFilter('active');
    setTypeFilter(null);
  }, [setStatusFilter, setTypeFilter]);

  const toolbarContent = <FilterBar quickFilters={quickFilters} onResetAll={handleResetAll} />;

  const renderMobileCard = useCallback(row => {
    const typeLabel = row.customer_type?.name ?? null;
    return <CustomerListItem row={row} typeLabel={typeLabel} />;
  }, []);

  return (
    <EntityGrid
      basePath="customers"
      fieldOverrides={fieldOverrides}
      actionHandlers={actionHandlers}
      extraParams={extraParams}
      options={{
        searchPlaceholder: t('customer.searchCustomers', 'Search customers'),
        persistKey: 'customerGrid',
        infiniteScroll: true,
        createAction: {
          label: `${t('general.create', 'Create')} ${tenantInfo.customer_term || t('customer.customer', 'Customer')}`,
          onClick: () => navigate('/support/customers/new'),
        },
        toolbarContent,
        statCards: {
          stats: gridStats,
          isLoading: statsLoading,
          visibility: statCardVisibility,
          order: statCardOrder,
        },
        terms: tenantTerms,
        mobile: { renderCard: renderMobileCard },
        columnVisibility,
        countNoun: t('customer.customers', 'customers'),
        export: exportOptions,
      }}
    />
  );
}
